// Tells the pages that the agent changed a model's rules, or its base or tiers.
//
// The same reason as `signalsRefreshBus`: the agent writes through the ordinary
// routes, so the change lands exactly as a person's would, and the page has no idea
// it happened. The emitter is the chat panel and the listeners are the Rules page,
// Fine tuning and the model page itself, none of them an ancestor of another.
//
// Built from the signals bus rather than copied: both are one document per model,
// with no target to carry, and a run of writes coalescing into one reload.
import { createSignalsRefreshBus } from './signalsRefreshBus';

const rules = createSignalsRefreshBus();
const model = createSignalsRefreshBus();
const datasetLoading = createSignalsRefreshBus();

/** The agent changed the rules: the Rules page reloads them. */
export const emitRulesRefresh = rules.emit;
export const onRulesRefresh = rules.subscribe;

/** The agent changed the base or the thresholds: the model and Fine tuning reload. */
export const emitModelRefresh = model.emit;
export const onModelRefresh = model.subscribe;

/**
 * The agent started loading this model's dataset (rgip `springbok_load_dataset`): the
 * model page switches to its loading screen, as it does when a load is started from the
 * dataset page. Emitted only on a load that actually started, which rgip marks with a
 * `dataset` receipt: a refused confirm makes the same tool call and carries none.
 */
export const emitDatasetLoading = datasetLoading.emit;
export const onDatasetLoading = datasetLoading.subscribe;

// A rule group the chat linked to. Held until the Rules page subscribes, because the link
// navigates there first and the page mounts after the click, the same arrangement as
// `askBus`: emitting cannot be too early.
let pendingGroup: number | null = null;
const groupHandlers = new Set<(group: number) => void>();

/** Open and scroll to rule group `group` (1-based) on the Rules page. */
export function focusRuleGroup(group: number): void {
  if (groupHandlers.size) {
    groupHandlers.forEach((handler) => handler(group));
    return;
  }
  pendingGroup = group;
}

export function onRuleGroupFocus(handler: (group: number) => void) {
  groupHandlers.add(handler);
  if (pendingGroup !== null) {
    const group = pendingGroup;
    pendingGroup = null;
    handler(group);
  }
  return () => {
    groupHandlers.delete(handler);
  };
}
